import React, { useState } from 'react'
import { CVRParticipant } from '../../types'
import { participantRowKey } from '../../utils/participantKey'
import { Section } from '../ui'
import ParticipantRow, { StripExpandLink, StripHistoricDivider, StripSectionHeader } from './ParticipantRow'

/**
 * Profile-page section showing the registered beneficial owners
 * ("reelle ejere"). This is the headline section of the page — the
 * single most important journalistic signal — so it is rendered in
 * emphasized form (rust-colored left rail via Section).
 *
 *   - Active owners are always visible.
 *   - Historic owners are folded behind a toggle.
 *   - When there are no active owners, we say so explicitly: that's
 *     itself a useful signal for a journalist (unregistered ownership).
 */
interface BeneficialOwnersProps {
  owners: CVRParticipant[]
  /** Omit outer Section — use inside Ejerskab tab composition. */
  embedded?: boolean
  /** Kompakt tekster til `EjerskabOverview`. */
  layout?: 'default' | 'overview'
  /** Column header for ownership strip (title + toggle in header row). */
  stripHeader?: { title: string; suffix?: string | null }
}

const BeneficialOwners: React.FC<BeneficialOwnersProps> = ({
  owners,
  embedded = false,
  layout = 'default',
  stripHeader,
}) => {
  const [showHistoric, setShowHistoric] = useState(false)
  const overview = layout === 'overview'

  const active = owners.filter((p) => p.role.isActive)
  const historic = owners.filter((p) => !p.role.isActive)

  const activeBlock =
    active.length === 0 ? (
      <p
        className={
          embedded || overview
            ? 'text-xs text-ink-500'
            : 'text-sm bg-signal-50 border border-signal-200 text-signal-900 rounded-sm px-3 py-2'
        }
      >
        {overview
          ? historic.length > 0
            ? 'Ingen aktuelle reelle ejere — kun historiske.'
            : 'Ingen registrerede reelle ejere.'
          : `Ingen aktuelle reelle ejere registreret${
              historic.length > 0 ? ' — kun historiske poster findes.' : '.'
            }`}
      </p>
    ) : (
      <ul className={overview ? 'list-none space-y-0.5' : 'list-none'}>
        {active.map((p, i) => (
          <ParticipantRow
            key={participantRowKey('bo-active', p, i)}
            participant={p}
            variant={overview ? 'strip' : 'owner'}
          />
        ))}
      </ul>
    )

  const historicBlock = showHistoric && historic.length > 0 && (
    <>
      {overview && <StripHistoricDivider />}
      <ul className={overview ? 'list-none space-y-0.5' : 'list-none mt-3 border-l-2 border-rule pl-4'}>
        {historic.map((p, i) => (
          <ParticipantRow
            key={participantRowKey('bo-historic', p, i)}
            participant={p}
            variant={overview ? 'strip' : 'owner'}
            forceHistoric
            stripExpanded={overview}
          />
        ))}
      </ul>
    </>
  )

  const historicToggle = historic.length > 0 && (
    <StripExpandLink expanded={showHistoric} onToggle={() => setShowHistoric((v) => !v)}>
      {showHistoric ? 'Skjul' : 'Vis'}{' '}
      {overview ? 'historiske' : 'historiske reelle ejere'} ({historic.length})
    </StripExpandLink>
  )

  const body =
    overview && embedded ? (
      <>
        {stripHeader && (
          <StripSectionHeader
            title={stripHeader.title}
            suffix={stripHeader.suffix}
            action={historicToggle}
          />
        )}
        {activeBlock}
        {historicBlock}
      </>
    ) : (
      <>
        {activeBlock}
        {historic.length > 0 && (
          <div className={overview ? 'mt-1.5' : 'mt-3'}>
            <button
              type="button"
              onClick={() => setShowHistoric((v) => !v)}
              className={
                overview
                  ? 'text-[10px] text-ink-600 hover:text-ink underline underline-offset-2'
                  : 'text-xs text-ink-700 hover:text-ink underline underline-offset-4 decoration-rule-strong hover:decoration-signal'
              }
              aria-expanded={showHistoric}
            >
              {showHistoric ? 'Skjul' : 'Vis'}{' '}
              {overview ? 'historiske' : 'historiske reelle ejere'} ({historic.length})
            </button>
            {historicBlock}
          </div>
        )}
      </>
    )

  if (embedded) {
    return body
  }

  return (
    <Section title="Reelle ejere" emphasis compact>
      {body}
    </Section>
  )
}

export default BeneficialOwners
